import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { css } from '@styled-system/css'
import { useField } from 'formik'

import { useSubscribeContext } from 'context'

import { Badge } from 'ui/bend/elements'
import { Caption, P } from 'ui/bend/typography'
import Text from 'ui/bend/typography/Text'

import RadioButton, {
  RadioButtonContainer,
  RadioButtonLabel,
  RadioButtonInput
} from '../FormElements/RadioButton'

const Details = styled(P)`
  color: ${(props) => props.theme.colors.grey6};

  ${RadioButtonContainer}:hover & {
    color: ${(props) => props.theme.colors.primary};
  }

  ${RadioButtonInput}:checked + ${RadioButtonLabel} & {
    color: ${(props) => props.theme.colors.primary};
    font-weight: 600;
  }

  ${RadioButtonInput}:disabled + ${RadioButtonLabel} & {
    color: ${(props) => props.theme.colors.grey4};
  }
`

const Price = styled(Text)(
  css({
    fontFamily: 'Proxima',
    fontSize: '18px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '24px' /* 133.333% */,
    letterSpacing: '0.396px'
  })
)

const MembershipPlan = ({ plan, ...props }) => {
  const { isAnOffer } = props
  const { actions } = useSubscribeContext()

  const id = `membership_plan_${plan.id}`
  const inputProps = {
    id: id,
    value: plan.id,
    ...props
  }
  const [field] = useField({ ...inputProps, type: 'radio' })

  const planTitle = () => {
    switch (plan.frequency) {
      case 'yearly':
        return 'annually'
      default:
        return plan.frequency
    }
  }

  const handleSelect = (e) => {
    if (!field.checked) {
      actions.membership.selectPlan(e.target.value)
      field.onChange(e)
    }
  }

  return (
    <RadioButton
      id={id}
      {...field}
      {...inputProps}
      onChange={handleSelect}
      containerProps={{ mb: '18px' }}
    >
      <Details>
        <span
          style={{
            textTransform: 'capitalize',
            fontSize: '24px',
            fontStyle: 'normal',
            fontWeight: 600,
            lineHeight: '32px'
          }}
        >
          {planTitle()}
        </span>
        {!isAnOffer && plan.comparisonDiscount && (
          <Badge
            style={{
              fontFamily: 'Proxima-Nova',
              fontSize: '12px',
              fontStyle: 'normal',
              fontWeight: '400',
              lineHeight: '14px',
              letterSpacing: '0.84px'
            }}
            variant={plan.featured ? 'featured' : 'solid'}
            ml='8px'
          >
            Save {plan.comparisonDiscount}
          </Badge>
        )}
      </Details>

      <Caption display='inline-block' color='grey5' my='4px'>
        {plan.description}
      </Caption>

      <Details
        style={{
          fontSize: '18px',
          fontStyle: 'normal',
          fontWeight: '400',
          lineHeight: '24px',
          letterSpacing: '0.396px'
        }}
      >
        <>
          <Price>${plan.equivalentMonthlyPrice}</Price> / month
          {plan.frequency !== 'monthly' && ` billed ${planTitle()}`}
        </>
      </Details>
    </RadioButton>
  )
}

MembershipPlan.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string.isRequired,
    frequency: PropTypes.string.isRequired,
    comparisonMonthlyPrice: PropTypes.string,
    comparisonDiscount: PropTypes.string,
    equivalentMonthlyPrice: PropTypes.string.isRequired,
    featured: PropTypes.bool.isRequired,
    trialEndDate: PropTypes.string,
    rolloverEndDate: PropTypes.string,
    couponApplies: PropTypes.bool,
    description: PropTypes.string.isRequired
  }).isRequired
}

export default MembershipPlan
